/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, PagedTable, PagedTableProps, Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import { HLabel } from 'components/HLabel'
import { Cns, Cpf, Telefone } from 'components/label'
import { TableBox } from 'components/table/TableBox'
import { VisualizarPaginaButton } from 'components/VisualizarPaginaButton'
import { intervalToDuration } from 'date-fns'
import { RiscoVulnerabilidadeIvcfEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment } from 'react'
import { dateAsYyyyMmDd, formatDate } from 'util/date/formatDate'
import { formatDurationToMonthsAndDays } from 'util/date/formatDuration'
import { humanizeDate } from 'util/date/humanize-date'
import { IVCF_RESULTADOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'

import { OldCidadaoListagemGeralQueryModel } from '../../model/model-acompanhamentoCondicaoSaudeListaTematica'

interface OldAcompanhamentoCondicaoSaudeListingProps
  extends Omit<PagedTableProps<OldCidadaoListagemGeralQueryModel>, 'columns'> {
  hasIvcfFilter?: boolean
}

export function AcompanhamentoCondicaoSaudeListing(props: Readonly<OldAcompanhamentoCondicaoSaudeListingProps>) {
  const { hasIvcfFilter, ...tableProps } = props
  const { getServerTimeNow } = useServerTime()

  const renderCidadao = (row: OldCidadaoListagemGeralQueryModel) => {
    const nascimento = dateAsYyyyMmDd(new Date(row.dataNascimentoCidadao))
    const formattedNascimento = formatDate(nascimento)
    const idade = humanizeDate(nascimento, getServerTimeNow().getTime())
    const cpfComponent = row.cpfCidadao && (
      <HFlow hSpacing={0.25} alignItems='center'>
        <Text fontWeight='bold'>CPF</Text>
        <Cpf value={row.cpfCidadao} />
      </HFlow>
    )
    const cnsComponent = row.cnsCidadao && (
      <HFlow hSpacing={0.25} alignItems='center'>
        <Text fontWeight='bold'>CNS</Text>
        <Cns value={row.cnsCidadao} />
      </HFlow>
    )

    return (
      <VFlow vSpacing={0}>
        <Text fontWeight='bold'>{row.nomeSocialCidadao?.titleCase() ?? row.nomeCidadao?.titleCase()}</Text>
        {cpfComponent ?? cnsComponent ?? <Text>Cidadão sem CNS e CPF</Text>}
        <Text>
          {idade} | {formattedNascimento}
        </Text>
      </VFlow>
    )
  }

  const renderTelefone = (row: OldCidadaoListagemGeralQueryModel) => {
    return !row?.telefoneCelular && !row?.telefoneContato && !row?.telefoneResidencial ? (
      <Text>-</Text>
    ) : (
      <span>
        {row.telefoneResidencial && (
          <Fragment>
            <Telefone value={row.telefoneResidencial} /> <br />
          </Fragment>
        )}
        {row.telefoneCelular && (
          <Fragment>
            <Telefone value={row.telefoneCelular} /> <br />
          </Fragment>
        )}
        {row.telefoneContato && (
          <Fragment>
            <Telefone value={row.telefoneContato} /> <br />
          </Fragment>
        )}
      </span>
    )
  }

  const renderUltimoAtend = ({
    dataUltimoAtendimentoMedico,
    dataUltimoAtendimentoEnfermagem,
    dataUltimaVisitaDomiciliar,
  }: OldCidadaoListagemGeralQueryModel) => {
    const atendimentos = [
      { title: 'Médica:', data: dataUltimoAtendimentoMedico },
      { title: 'Enfermagem:', data: dataUltimoAtendimentoEnfermagem },
      { title: 'Visita domiciliar:', data: dataUltimaVisitaDomiciliar },
    ]

    const now = getServerTimeNow()

    return (
      <VFlow vSpacing={0}>
        {atendimentos.map(({ title, data }) => {
          const parsedDate = data ? new Date(data) : null

          return (
            <HLabel key={title} title={title} placeholder='-'>
              {parsedDate && formatDurationToMonthsAndDays(intervalToDuration({ start: parsedDate, end: now }))}
            </HLabel>
          )
        })}
      </VFlow>
    )
  }

  const renderIvcf = (row: OldCidadaoListagemGeralQueryModel) => {
    return !row?.ivcf?.dataResultado && !row?.ivcf?.riscoVulnerabilidade ? (
      <Text>Sem registros</Text>
    ) : (
      <VFlow vSpacing={0}>
        {row?.ivcf?.riscoVulnerabilidade && <Text>{colorLabel(row.ivcf.riscoVulnerabilidade)}</Text>}
        {row?.ivcf?.dataResultado && (
          <VFlow vSpacing={0}>
            <Text>Registrado em</Text>
            <DateTime value={row.ivcf?.dataResultado} format='DD/MM/YYYY' />
          </VFlow>
        )}
      </VFlow>
    )
  }

  const styles = createStyles()

  return (
    <TableBox>
      <PagedTable<OldCidadaoListagemGeralQueryModel>
        {...tableProps}
        columns={[
          {
            name: 'cidadao',
            header: 'Cidadão',
            sortable: true,
            render: renderCidadao,
            style: css`
              width: 18rem;
              padding: 1rem;
            `,
          },
          {
            name: 'endereco',
            header: 'Endereço',
            sortable: true,
            render: (row: OldCidadaoListagemGeralQueryModel) => row.endereco.titleCase(),
            style: css`
              width: 22rem;
            `,
          },
          {
            name: 'microarea',
            header: 'Microárea',
            align: 'center',
            sortable: true,
            render: (row: OldCidadaoListagemGeralQueryModel) => row.microarea ?? '-',
            style: css`
              width: 6rem;
            `,
          },
          {
            name: 'telefone',
            header: 'Telefone',
            sortable: false,
            render: renderTelefone,
            style: css`
              width: 12rem;
            `,
          },
          {
            name: 'ultimasConsultas',
            header: 'Últimas consultas',
            sortable: false,
            render: renderUltimoAtend,
            style: css`
              width: 18rem;
              padding: 1rem;
            `,
          },
          {
            name: 'pressaoArterial',
            header: 'Última P.A. (mmHg)',
            sortable: false,
            render: (row: OldCidadaoListagemGeralQueryModel) => row.ultimaMedicaoPressaoArterial ?? '-',
            style: css`
              width: 8rem;
            `,
          },
          ...(hasIvcfFilter
            ? [
                {
                  name: 'ivcf',
                  header: 'IVCF-20',
                  sortable: false,
                  render: renderIvcf,
                  style: styles.ivcfColumn,
                },
              ]
            : []),
          {
            name: 'visualizarCidadao',
            header: '',
            render: (row: OldCidadaoListagemGeralQueryModel) => (
              <VisualizarPaginaButton path={`/cidadao/${row.idCidadao}/informacoes`} tooltipText='Visualizar cidadão' />
            ),
            style: { width: '4rem' },
          },
        ]}
      />
    </TableBox>
  )
}

export const colorLabel = (ivcfRiscoVulnerabilidade: RiscoVulnerabilidadeIvcfEnum) => {
  const ivcfStyles = createStyles(ivcfRiscoVulnerabilidade)
  return (
    <HFlow hSpacing={0}>
      <div
        css={css([
          ivcfStyles.indiceIvcf,
          ivcfStyles.ivcfLabel,
          css`
            width: 1rem;
            height: 1rem;
            border-radius: 0.125rem;
            margin-right: 0.5rem;
          `,
        ])}
      />
      {ivcfRiscoVulnerabilidade.capitalize()}
    </HFlow>
  )
}

const createStyles = (risco?: RiscoVulnerabilidadeIvcfEnum) => ({
  indiceIvcf: css`
    ${risco && `background: ${IVCF_RESULTADOS[risco].colorScale.c40};`}
  `,
  ivcfColumn: `
    width: 10rem;
  `,
  ivcfLabel: `
    width: 1rem;
    height: 1rem;
    border-radius: 0.125rem;
    margin-right: 0.5rem;
  `,
})
